import React, { useState } from "react";
import {
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import SearchIconBlack from "../../../../../../assets/images/SearchIconBlack.svg";
import CloseIcon from "../../../../../../assets/images/CloseIcon.svg";
import Colors from "../../../constants/colors";
import { ComponentStyles } from "../../../constants/componentStyles";
import { inputAssistDisabledProps } from "./FloatingTextInputComponent";
import { useTranslation } from "react-i18next";

export type SearchInputComponentProps = {
  value?: string;
  placeholder?: string;
  onChangeText?: (text: string) => void;
  testID?: string;
  editable?: boolean;
  /** Red border (e.g. validation). Takes precedence over focus ring when true. */
  error?: boolean;
  /** Optional message under the field when `error` */
  errorText?: string | null;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Outer margin top; default matches previous usage */
  marginTop?: number;
  isAutoFocus?: boolean;
};

const SearchInputComponent: React.FC<SearchInputComponentProps> = ({
  value,
  placeholder,
  onChangeText,
  testID,
  editable = true,
  error = false,
  errorText,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  marginTop = 16,
  isAutoFocus = false,
}) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = Boolean((value ?? "").trim().length);
  const isDisabled = !editable;
  const showClear = hasValue && editable && Boolean(onChangeText);

  const showFocusRing = isFocused && !isDisabled && !error;
  const showErrorRing = Boolean(error) && !isDisabled;

  const borderColor = showErrorRing
    ? Colors.errorRedColor
    : showFocusRing
      ? Colors.titleTextColor
      : "transparent";
  const borderWidth = isDisabled ? 0 : showErrorRing || showFocusRing ? 1 : 0;

  const handleClear = () => {
    onChangeText?.("");
  };

  return (
    <>
      <View
        style={[
          styles.searchContainer,
          { marginTop, borderColor, borderWidth },
          isDisabled && styles.searchContainerDisabled,
        ]}
      >
        <View style={[styles.iconWrap, isDisabled && styles.iconDimmed]}>
          <SearchIconBlack width={24} height={24} />
        </View>
        <TextInput
          testID={testID}
          style={[
            styles.searchInput,
            showClear && styles.searchInputWithClear,
            isDisabled && styles.searchInputDisabled,
          ]}
          placeholder={placeholder}
          placeholderTextColor={Colors.subTitleColor}
          value={value}
          onChangeText={onChangeText}
          editable={editable}
          autoFocus={Boolean(isAutoFocus)}
          selectionColor={Colors.black}
          cursorColor={Colors.black}
          onFocus={() => {
            setIsFocused(true);
            onFocusProp?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            onBlurProp?.();
          }}
          {...inputAssistDisabledProps}
        />
        {showClear ? (
          <TouchableOpacity
            testID={testID ? `${testID}-clear` : "searchInputClear"}
            style={styles.clearButton}
            onPress={handleClear}
            accessibilityLabel={t("CLEAR_SEARCH_ACCESSIBILITY")}
            {...({ onMouseDown: (e: any) => e.preventDefault() } as any)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <CloseIcon width={24} height={24} />
          </TouchableOpacity>
        ) : null}
      </View>
      {errorText ? <Text style={styles.errorText}>{errorText}</Text> : null}
    </>
  );
};

export default SearchInputComponent;

const styles = StyleSheet.create({
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "stretch",
    width: "100%",
    minWidth: 0,
    backgroundColor: Colors.textInputBackground,
    borderRadius: 9999,
    gap: 4,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderStyle: "solid",
    ...(Platform.OS === "web"
      ? ({
          boxSizing: "border-box" as const,
          transitionProperty: "border-color, border-width, opacity, background-color",
          transitionDuration: "150ms",
          transitionTimingFunction: "ease-in-out",
        } as any)
      : {}),
  },
  searchContainerDisabled: {
    backgroundColor: Colors.searchCompanyBackground,
    opacity: 0.85,
  },
  iconWrap: {
    justifyContent: "center",
    alignItems: "center",
  },
  iconDimmed: {
    opacity: 0.45,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    color: Colors.titleTextColor,
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: 24,
    letterSpacing: 0,
    paddingLeft: 4,
    paddingVertical: 0,
    /** RN Web: outline must live on `style` so the real <input> loses the UA blue focus ring. */
    ...(Platform.OS === "web"
      ? ({
          outline: "none",
          outlineWidth: 0,
          outlineStyle: "none",
          outlineOffset: 0,
          outlineColor: "transparent",
          borderWidth: 0,
          borderStyle: "none",
          borderColor: "transparent",
          boxShadow: "none",
          backgroundColor: "transparent",
        } as any)
      : {}),
  },
  searchInputWithClear: {
    paddingRight: 40,
  },
  searchInputDisabled: {
    color: Colors.applyText,
  },
  clearButton: {
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: 4,
  },
  errorText: {
    marginTop: 6,
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    lineHeight: 16,
    color: Colors.errorRedColor,
  },
});
